import React from "react";
import { DateTime } from "luxon";
import { Trans, useTranslation } from "react-i18next";
import { useRetrieveCurrentPlanzone } from "@planzone/view-models";
import { Button } from "@common/button";
import { MemberAvatar } from "@common/memberAvatar";
import { Props } from "./types";
import styles from "./styles.scss";

function AllocationWarnings({ warnings, date, onConfirm, close }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const userTimezone = DateTime.local().zoneName;

  const dateDt = date
    ? DateTime.fromISO(date, { zone: "utc" }).setZone(userTimezone)
    : null;
  const formattedDate =
    dateDt && dateDt.isValid ? dateDt.toLocaleString(DateTime.DATE_SHORT) : "";
  const amplitudeThreshold = planzone.criticalDailyAmplitudeHours ?? "?";
  const volumeThreshold = planzone.criticalDailyVolumeHours ?? "?";
  const restThreshold = planzone.criticalRestPeriodHours ?? "?";

  return (
    <div className={styles.allocationWarnings}>
      <div className={styles.header}>
        <p className={styles.title}>{t("allocationWarnings.title")}</p>
      </div>
      <div className={styles.body}>
        <p className={styles.intro}>{t("allocationWarnings.intro")}</p>
        <ul className={styles.list}>
          {warnings.map((warning) => (
            <li key={warning.resourceUuid} className={styles.item}>
              <div className={styles.resource}>
                <div className={styles.avatar}>
                  <MemberAvatar name={warning.resourceName} avatarUrl="" />
                </div>
                <p className={styles.resourceName}>{warning.resourceName}</p>
              </div>
              <ul className={styles.warningDetails}>
                {warning.hasDailyAmplitudeWarning && (
                  <li>
                    {t("allocationWarnings.hasDailyAmplitudeWarning", {
                      threshold: amplitudeThreshold,
                    })}{" "}
                    {formattedDate}
                  </li>
                )}
                {warning.hasDailyVolumeWarning && (
                  <li>
                    {t("allocationWarnings.hasDailyVolumeWarning", {
                      threshold: volumeThreshold,
                    })}{" "}
                    {formattedDate}
                  </li>
                )}
                {warning.hasRestPeriodWarning && (
                  <li>
                    {t("allocationWarnings.hasRestPeriodWarning", {
                      threshold: restThreshold,
                    })}{" "}
                    {formattedDate}
                  </li>
                )}
                {warning.hasUnavailabilityWarning && (
                  <li>
                    <Trans
                      i18nKey="allocationWarnings.hasUnavailabilityWarning"
                      values={{
                        resourceName: warning.resourceName,
                        unavailabilityLabel: warning.unavailabilityLabel,
                        unavailabilityDurationHours:
                          warning.unavailabilityDurationHours,
                      }}
                      components={{ bold: <strong /> }}
                    />
                  </li>
                )}
              </ul>
            </li>
          ))}
        </ul>
        <div className={styles.actions}>
          <Button press={close} type="cancel">
            {t("allocationWarnings.cancel")}
          </Button>
          <Button press={onConfirm} type="submit">
            {t("allocationWarnings.confirm")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export { AllocationWarnings };
